import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';

export type CarouselKind = 'image' | 'icon';

/**
 * One item of the Home carousel as kredit-api-MT serves it (`GET /api/app-config/carrusel/v2`).
 *
 * Items authored from this screen carry `kind` and the v2 fields. Items the Ionic-era console
 * wrote carry `legacy: true`, an id derived from their title and only their old keys; the
 * backend lets them be deleted and nothing else.
 */
export interface CarouselItem {
    id: string;
    legacy: boolean;
    kind?: CarouselKind;
    title: string;
    description?: string;
    icon?: string;
    cta_label?: string;
    alt_text?: string;
    image_url?: string;
    image_public_id?: string;
    image_width?: number | null;
    image_height?: number | null;
    image_bytes?: number | null;
    action_type?: string;
    action_target?: string;
    starts_at?: string | null;
    expires_at?: string | null;
    order: number;
    status: boolean;
    created_at?: string | null;
    created_by?: string;
    updated_at?: string | null;
    updated_by?: string;
    /** Ionic-era items only: the free-text action they carried. */
    action?: string;
}

/** Validation limits the backend applies; the editor mirrors them before uploading. */
export interface CarouselLimits {
    max_active_per_kind: number;
    image: {
        max_bytes: number;
        min_width: number;
        ratio: string;
        ratio_tolerance: number;
        recommended_width: number;
        recommended_max_bytes: number;
        formats: string[];
    };
    icons: string[];
    text: {
        title: number;
        title_with_cta: number;
        description: number;
        cta_label: number;
        alt_text: number;
        image_name: number;
    };
}

export interface CarouselPayload {
    /** Global switch of the carousel. */
    status: boolean;
    list: CarouselItem[];
    active_by_kind: Record<CarouselKind, number>;
    limits: CarouselLimits;
}

export interface CarouselItemResult {
    item: CarouselItem;
    /** Non-blocking remarks from the backend (image below the recommended size, sixth active banner...). */
    warnings: string[];
}

export interface CarouselEnvelope<T> {
    ok: boolean;
    message?: string;
    data: T;
}

/** Fields the editor sends; the backend validates them and answers 400 with a Spanish message. */
export interface CarouselItemFields {
    kind?: CarouselKind;
    title: string;
    description: string;
    icon: string;
    cta_label: string;
    alt_text: string;
    action_type: string;
    action_target: string;
    starts_at: string;
    expires_at: string;
    status: boolean;
}

/**
 * Home carousel of the Flutter app — the v2 routes kredit-api-MT added for DK-53.
 *
 * Multipart calls (create, replace image) set no content type: the browser adds the boundary.
 */
@Injectable({
    providedIn: 'root'
})
export class CarouselConfigService {
    private readonly baseUrl: string;
    private readonly jsonHeaders = { 'content-type': 'application/json' };

    constructor(private httpClient: HttpClient) {
        this.baseUrl = environment.BASE_LA;
    }

    /** GET /api/app-config/carrusel/v2 */
    getCarousel = (): Promise<CarouselEnvelope<CarouselPayload>> =>
        lastValueFrom(
            this.httpClient.get<CarouselEnvelope<CarouselPayload>>(`${this.baseUrl}/api/app-config/carrusel/v2`, {
                headers: this.jsonHeaders,
            })
        );

    /** PUT /api/app-config/carrusel/toggle-status — the global switch, unchanged since the Ionic era. */
    setGlobalStatus = (status: boolean): Promise<CarouselEnvelope<unknown>> =>
        lastValueFrom(
            this.httpClient.put<CarouselEnvelope<unknown>>(
                `${this.baseUrl}/api/app-config/carrusel/toggle-status`,
                { status },
                { headers: this.jsonHeaders }
            )
        );

    /** POST /api/app-config/carrusel/v2/items (multipart: fields + `image` for an image banner) */
    createItem = (formData: FormData): Promise<CarouselEnvelope<CarouselItemResult>> =>
        lastValueFrom(
            this.httpClient.post<CarouselEnvelope<CarouselItemResult>>(
                `${this.baseUrl}/api/app-config/carrusel/v2/items`,
                formData
            )
        );

    /** PUT /api/app-config/carrusel/v2/items/:id (JSON: text, action, validity window, status) */
    updateItem = (id: string, fields: Partial<CarouselItemFields>): Promise<CarouselEnvelope<CarouselItemResult>> =>
        lastValueFrom(
            this.httpClient.put<CarouselEnvelope<CarouselItemResult>>(
                `${this.baseUrl}/api/app-config/carrusel/v2/items/${encodeURIComponent(id)}`,
                fields,
                { headers: this.jsonHeaders }
            )
        );

    /** PUT /api/app-config/carrusel/v2/items/:id/image (multipart: `image`) */
    replaceItemImage = (id: string, formData: FormData): Promise<CarouselEnvelope<CarouselItemResult>> =>
        lastValueFrom(
            this.httpClient.put<CarouselEnvelope<CarouselItemResult>>(
                `${this.baseUrl}/api/app-config/carrusel/v2/items/${encodeURIComponent(id)}/image`,
                formData
            )
        );

    /** PUT /api/app-config/carrusel/v2/items/:id/status */
    setItemStatus = (id: string, status: boolean): Promise<CarouselEnvelope<CarouselItemResult>> =>
        lastValueFrom(
            this.httpClient.put<CarouselEnvelope<CarouselItemResult>>(
                `${this.baseUrl}/api/app-config/carrusel/v2/items/${encodeURIComponent(id)}/status`,
                { status },
                { headers: this.jsonHeaders }
            )
        );

    /** PUT /api/app-config/carrusel/v2/reorder — the FULL list of ids, Ionic-era ones included. */
    reorder = (ids: string[]): Promise<CarouselEnvelope<{ list: CarouselItem[] }>> =>
        lastValueFrom(
            this.httpClient.put<CarouselEnvelope<{ list: CarouselItem[] }>>(
                `${this.baseUrl}/api/app-config/carrusel/v2/reorder`,
                { ids },
                { headers: this.jsonHeaders }
            )
        );

    /** DELETE /api/app-config/carrusel/v2/items/:id — also the Ionic-era items. */
    deleteItem = (id: string): Promise<CarouselEnvelope<{ deleted: { id: string; title: string; legacy: boolean }; remaining: number }>> =>
        lastValueFrom(
            this.httpClient.delete<CarouselEnvelope<{ deleted: { id: string; title: string; legacy: boolean }; remaining: number }>>(
                `${this.baseUrl}/api/app-config/carrusel/v2/items/${encodeURIComponent(id)}`,
                { headers: this.jsonHeaders }
            )
        );
}
